import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthProvider'
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon } from '@/components/shared/Icons'
import toast from 'react-hot-toast'

type ViewMode = 'login' | 'register' | 'forgot'

export default function LoginPage() {
  const { signIn, registerPublic, forgotPassword } = useAuth()
  const navigate = useNavigate()

  const [mode, setMode] = useState<ViewMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [nombre, setNombre] = useState('')
  const [codigoAcceso, setCodigoAcceso] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [showCodigo, setShowCodigo] = useState(false)

  function resetForm() {
    setError(null)
    setNombre('')
    setCodigoAcceso('')
    setPassword('')
    setShowPass(false)
    setShowCodigo(false)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    if (mode === 'login') {
      const { error } = await signIn(email, password)
      if (error) {
        setError('Email o contraseña incorrectos')
      } else {
        navigate('/', { replace: true })
      }
    } else if (mode === 'register') {
      const { error } = await registerPublic(email, password, nombre, codigoAcceso)
      if (error) {
        setError(error)
      } else {
        toast.success('Usuario registrado. Iniciando sesión...')
        const loginRes = await signIn(email, password)
        if (!loginRes.error) navigate('/', { replace: true })
      }
    } else if (mode === 'forgot') {
      const { error } = await forgotPassword(email, password)
      if (error) {
        setError(error)
      } else {
        toast.success('Contraseña actualizada con éxito.')
        setMode('login')
        resetForm()
      }
    }

    setLoading(false)
  }

  const iconStyle: React.CSSProperties = { color: 'var(--me-text-3)', flexShrink: 0 }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--me-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        <div style={{ marginBottom: 40 }}>
          <MELogoMark />
        </div>

        <div className="me-card" style={{ padding: 32 }}>
          <div style={{ marginBottom: 28 }}>
            <h1 style={{
              fontFamily: 'var(--me-display)',
              fontVariationSettings: '"opsz" 32, "wght" 600',
              fontSize: 26,
              letterSpacing: '-0.025em',
              margin: '0 0 6px',
            }}>
              {mode === 'login' && 'Iniciar sesión'}
              {mode === 'register' && 'Crear cuenta'}
              {mode === 'forgot' && 'Recuperar contraseña'}
            </h1>
            <p style={{ fontSize: 13.5, color: 'var(--me-text-3)', margin: 0 }}>
              {mode === 'login' && 'Sistema de gestión MayoExpress'}
              {mode === 'register' && 'Ingresá el código de acceso para registrarte'}
              {mode === 'forgot' && 'Ingresá tu email y la nueva contraseña'}
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Nombre — solo registro */}
            {mode === 'register' && (
              <div>
                <label className="me-label">Nombre completo</label>
                <div className="me-input">
                  <input
                    type="text"
                    placeholder="Tu nombre"
                    value={nombre}
                    onChange={e => setNombre(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="me-label">Email</label>
              <div className="me-input">
                <MailIcon size={17} style={iconStyle} />
                <input
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus={mode !== 'register'}
                />
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <label className="me-label">
                {mode === 'forgot' ? 'Nueva contraseña' : 'Contraseña'}
              </label>
              <div className="me-input">
                <LockIcon size={17} style={iconStyle} />
                <input
                  type={showPass ? 'text' : 'password'}
                  placeholder={mode === 'forgot' ? 'Mínimo 6 caracteres' : '••••••••'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  {showPass ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
                </button>
              </div>
            </div>

            {/* Código de acceso — solo registro */}
            {mode === 'register' && (
              <div>
                <label className="me-label">Código de acceso</label>
                <div className="me-input">
                  <LockIcon size={17} style={iconStyle} />
                  <input
                    type={showCodigo ? 'text' : 'password'}
                    placeholder="Código proporcionado por el administrador"
                    value={codigoAcceso}
                    onChange={e => setCodigoAcceso(e.target.value)}
                    required
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCodigo(v => !v)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', display: 'flex', alignItems: 'center', padding: 0 }}
                  >
                    {showCodigo ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div style={{
                fontSize: 12.5,
                color: 'var(--me-bad)',
                background: 'var(--me-bad-soft)',
                border: '1px solid var(--me-bad)',
                borderRadius: 8,
                padding: '8px 12px',
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="me-btn me-btn-primary"
              style={{ width: '100%', marginTop: 4 }}
              disabled={loading}
            >
              {loading ? 'Procesando...' : (
                mode === 'login' ? 'Ingresar' :
                mode === 'register' ? 'Crear cuenta' : 'Cambiar contraseña'
              )}
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, alignItems: 'center' }}>
              {mode === 'login' && (
                <>
                  <button type="button"
                    onClick={() => { setMode('forgot'); resetForm() }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', fontSize: 13 }}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                  <button type="button"
                    onClick={() => { setMode('register'); resetForm() }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', fontSize: 13 }}
                  >
                    Registrar nuevo usuario
                  </button>
                </>
              )}
              {mode !== 'login' && (
                <button type="button"
                  onClick={() => { setMode('login'); resetForm() }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', fontSize: 13 }}
                >
                  Volver al inicio de sesión
                </button>
              )}
            </div>
          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--me-text-4)', marginTop: 24 }}>
          MayoExpress · Sistema de gestión 2026 · Desarrollado por Walter I. Neira
        </p>
      </div>
    </div>
  )
}

function MELogoMark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 42,
        height: 42,
        borderRadius: 12,
        background: 'var(--me-acid)',
        color: 'var(--me-acid-ink)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--me-display)',
        fontWeight: 700,
        fontSize: 22,
        letterSpacing: '-0.04em',
        clipPath: 'polygon(0 0, 100% 0, 100% 75%, 75% 100%, 0 100%)',
      }}>M</div>
      <span style={{
        fontFamily: 'var(--me-display)',
        fontVariationSettings: '"opsz" 32, "wght" 700',
        fontSize: 22,
        letterSpacing: '-0.03em',
        color: 'var(--me-text)',
      }}>MayoExpress</span>
    </div>
  )
}
